import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { getSequenceTemplatesPromise } from "../../services/apiService";
import SequenceCanvas from "./SequenceCanvas";

export default function TemplateBrowserModal({ open, onClose, onImport }) {
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("abmfy");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getSequenceTemplatesPromise()
      .then((res) => {
        const list = res.data?.templates || res.data || [];
        setTemplates(Array.isArray(list) ? list : []);
        if (list[0]) setSelectedId(list[0].id);
      })
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates.filter((t) => {
      if (!q) return true;
      return (
        (t.name || "").toLowerCase().includes(q)
        || (t.description || "").toLowerCase().includes(q)
      );
    });
  }, [templates, search]);

  const selected = filtered.find((t) => t.id === selectedId) || filtered[0];

  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-6xl h-[85vh] flex flex-col shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="text-lg font-semibold">Browse templates</h2>
          <button type="button" onClick={onClose} className="text-gray-500 text-xl">
            ×
          </button>
        </div>
        <div className="flex flex-1 min-h-0">
          <div className="w-80 border-r flex flex-col">
            <div className="p-3 flex gap-2">
              <button
                type="button"
                onClick={() => setTab("abmfy")}
                className={`flex-1 text-xs py-2 rounded-lg border ${
                  tab === "abmfy" ? "bg-white border-gray-300 shadow-sm" : "border-transparent bg-gray-50"
                }`}
              >
                ABMFY templates
              </button>
              <button
                type="button"
                onClick={() => setTab("campaign")}
                className={`flex-1 text-xs py-2 rounded-lg border ${
                  tab === "campaign" ? "bg-white border-gray-300 shadow-sm" : "border-transparent bg-gray-50"
                }`}
              >
                Campaign templates
              </button>
            </div>
            <div className="px-3 pb-2">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search templates"
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-2">
              {loading ? (
                <div className="text-sm text-gray-400 p-3">Loading…</div>
              ) : filtered.length === 0 ? (
                <div className="text-sm text-gray-400 p-3">No templates found</div>
              ) : (
                filtered.map((tpl) => (
                  <button
                    key={tpl.id}
                    type="button"
                    onClick={() => setSelectedId(tpl.id)}
                    className={`w-full text-left rounded-xl border p-3 ${
                      selected?.id === tpl.id
                        ? "border-blue-500 bg-blue-50/40"
                        : "border-gray-200 hover:border-blue-300"
                    }`}
                  >
                    <div className="text-sm font-semibold text-gray-900">{tpl.name}</div>
                    <div className="text-xs text-gray-500 mt-1 line-clamp-2">
                      {tpl.description || "Outreach sequence template"}
                    </div>
                    <div className="flex gap-2 mt-2">
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">
                        72%
                      </span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-50 text-green-600">
                        48%
                      </span>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            {selected ? (
              <SequenceCanvas
                steps={(selected.steps || []).map((s, i) => ({
                  ...s,
                  id: s.id || `tpl_${i}`,
                  order: s.order ?? i + 1,
                }))}
                readOnly
                autoLayout
              />
            ) : (
              <div className="h-full flex items-center justify-center text-gray-400">
                Select a template
              </div>
            )}
          </div>
        </div>
        <div className="px-5 py-4 border-t flex justify-between">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border text-sm">
            Close
          </button>
          <button
            type="button"
            disabled={!selected}
            onClick={() => selected && onImport?.(selected)}
            className="px-4 py-2 rounded-lg bg-blue-500 text-white text-sm disabled:opacity-50"
          >
            Import template
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
